import { Stack, Title, Text, Button, Group, Chip, ThemeIcon, Paper, SegmentedControl } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconFish, IconMeat, IconMeatOff } from '@tabler/icons-react';
import { useState, useEffect } from 'react';
import { FoodAutocompleteInput } from '../UI/FoodAutocompleteInput';
import { PROTEIN_SUGGESTIONS } from '../../utils/proteinSuggestions';
import { DEFAULT_PROTEIN_DIVERSITY } from '../../utils/familyProfileDefaults';

type ProteinPreferencesStepProps = {
  value: {
    favoriteProteins?: string[];
    avoidProteins?: string[];
    proteinDiversity?: "minimal" | "moderate" | "high";
  };
  onChange: (value: {
    favoriteProteins?: string[];
    avoidProteins?: string[];
    proteinDiversity?: "minimal" | "moderate" | "high";
  }) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isCompleting?: boolean;
};

const commonProteins = [
  { label: 'Chicken', icon: '🐔' },
  { label: 'Beef', icon: '🥩' },
  { label: 'Pork', icon: '🥓' },
  { label: 'Fish', icon: '🐟' },
  { label: 'Salmon', icon: '🍣' },
  { label: 'Shrimp', icon: '🦐' },
  { label: 'Turkey', icon: '🦃' },
  { label: 'Tofu', icon: '🥡' },
  { label: 'Beans', icon: '🫘' },
  { label: 'Eggs', icon: '🥚' },
  { label: 'Cheese', icon: '🧀' },
  { label: 'Nuts', icon: '🥜' },
];

export const ProteinPreferencesStep = ({
  value: proteinPreferences,
  onChange: onProteinPreferencesChange,
  onNext,
  onBack,
  isLastStep
}: ProteinPreferencesStepProps) => {
  const [favoriteInput, setFavoriteInput] = useState('');
  const [avoidInput, setAvoidInput] = useState('');
  const [pendingNext, setPendingNext] = useState(false);

  // Effect to call onNext after state has been updated
  useEffect(() => {
    if (pendingNext) {
      setPendingNext(false);
      onNext();
    }
  }, [proteinPreferences.favoriteProteins, proteinPreferences.avoidProteins, pendingNext, onNext]);

  const toggleFavoriteProtein = (protein: string) => {
    const currentFavorites = proteinPreferences.favoriteProteins || [];
    const currentAvoids = proteinPreferences.avoidProteins || [];
    
    const updatedFavorites = currentFavorites.includes(protein)
      ? currentFavorites.filter(p => p !== protein)
      : [...currentFavorites, protein];
    
    // Remove from avoid list if adding to favorites (case-insensitive)
    const updatedAvoids = updatedFavorites.includes(protein)
      ? currentAvoids.filter(p => p.toLowerCase() !== protein.toLowerCase())
      : currentAvoids;
    
    onProteinPreferencesChange({
      ...proteinPreferences,
      favoriteProteins: updatedFavorites,
      avoidProteins: updatedAvoids
    });
  };

  const toggleAvoidProtein = (protein: string) => {
    const currentAvoids = proteinPreferences.avoidProteins || [];
    const currentFavorites = proteinPreferences.favoriteProteins || [];
    
    const updatedAvoids = currentAvoids.includes(protein)
      ? currentAvoids.filter(p => p !== protein)
      : [...currentAvoids, protein];
    
    // Remove from favorites list if adding to avoid (case-insensitive)
    const updatedFavorites = updatedAvoids.includes(protein)
      ? currentFavorites.filter(p => p.toLowerCase() !== protein.toLowerCase())
      : currentFavorites;
    
    onProteinPreferencesChange({
      ...proteinPreferences,
      avoidProteins: updatedAvoids,
      favoriteProteins: updatedFavorites
    });
  };

  const addCustomFavorite = (text: string) => {
    if (!text.trim()) return;

    const predefinedLabels = commonProteins.map(p => p.label);

    // Split by comma and process each item
    const newProteins = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .filter(item => {
        // Check against existing favorites (case-insensitive)
        const alreadyInFavorites = (proteinPreferences.favoriteProteins || []).some(p => p.toLowerCase() === item.toLowerCase());
        // Check against predefined proteins (case-insensitive)
        const isPredefined = predefinedLabels.some(p => p.toLowerCase() === item.toLowerCase());
        return !alreadyInFavorites && !isPredefined;
      });

    // Handle predefined proteins by adding them directly
    const predefinedToAdd = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => predefinedLabels.find(p => p.toLowerCase() === item.toLowerCase()))
      .filter((protein): protein is string => Boolean(protein))
      .filter(protein => !(proteinPreferences.favoriteProteins || []).includes(protein));
    
    if (newProteins.length === 0 && predefinedToAdd.length === 0) {
      setFavoriteInput('');
      return;
    }
    
    const currentFavorites = proteinPreferences.favoriteProteins || [];
    const currentAvoids = proteinPreferences.avoidProteins || [];
    
    const allNewProteins = [...predefinedToAdd, ...newProteins];
    const updatedFavorites = [...currentFavorites, ...allNewProteins];
    // Remove from avoid list if adding to favorites (case-insensitive)
    const updatedAvoids = currentAvoids.filter(avoid => 
      !allNewProteins.some(newProtein => newProtein.toLowerCase() === avoid.toLowerCase())
    );
    
    onProteinPreferencesChange({
      ...proteinPreferences,
      favoriteProteins: updatedFavorites,
      avoidProteins: updatedAvoids
    });
    setFavoriteInput('');
  };

  const addCustomAvoid = (text: string) => {
    if (!text.trim()) return;

    const predefinedLabels = commonProteins.map(p => p.label);

    // Split by comma and process each item
    const newAvoidProteins = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
      .filter(item => {
        // Check against existing avoids (case-insensitive)
        const alreadyInAvoids = (proteinPreferences.avoidProteins || []).some(p => p.toLowerCase() === item.toLowerCase());
        // Check against predefined proteins (case-insensitive)
        const isPredefined = predefinedLabels.some(p => p.toLowerCase() === item.toLowerCase());
        return !alreadyInAvoids && !isPredefined;
      });

    // Handle predefined proteins by adding them directly
    const predefinedToAdd = text
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => predefinedLabels.find(p => p.toLowerCase() === item.toLowerCase()))
      .filter((protein): protein is string => Boolean(protein))
      .filter(protein => !(proteinPreferences.avoidProteins || []).includes(protein));
    
    if (newAvoidProteins.length === 0 && predefinedToAdd.length === 0) {
      setAvoidInput('');
      return;
    }
    
    const currentAvoids = proteinPreferences.avoidProteins || [];
    const currentFavorites = proteinPreferences.favoriteProteins || [];
    
    const allNewAvoidProteins = [...predefinedToAdd, ...newAvoidProteins];
    const updatedAvoids = [...currentAvoids, ...allNewAvoidProteins];
    // Remove from favorites list if adding to avoid (case-insensitive)
    const updatedFavorites = currentFavorites.filter(favorite => 
      !allNewAvoidProteins.some(newAvoid => newAvoid.toLowerCase() === favorite.toLowerCase())
    );
    
    onProteinPreferencesChange({
      ...proteinPreferences,
      avoidProteins: updatedAvoids,
      favoriteProteins: updatedFavorites
    });
    setAvoidInput('');
  };

  const removeFavorite = (protein: string) => {
    const updated = (proteinPreferences.favoriteProteins || []).filter(p => p !== protein);
    onProteinPreferencesChange({
      ...proteinPreferences,
      favoriteProteins: updated
    });
  };

  const removeAvoid = (protein: string) => {
    const updated = (proteinPreferences.avoidProteins || []).filter(p => p !== protein);
    onProteinPreferencesChange({
      ...proteinPreferences,
      avoidProteins: updated
    });
  };

  const handleNext = () => {
    let updated = false;
    let newValue = { ...proteinPreferences };

    // Auto-add text from "Other proteins" (favorites) input
    if (favoriteInput.trim()) {
      const predefinedLabels = commonProteins.map(p => p.label);
      const newProteins = favoriteInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
        .filter(item => {
          const alreadyInFavorites = (newValue.favoriteProteins || []).some(p => p.toLowerCase() === item.toLowerCase());
          const isPredefined = predefinedLabels.some(p => p.toLowerCase() === item.toLowerCase());
          return !alreadyInFavorites && !isPredefined;
        });

      const predefinedToAdd = favoriteInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => predefinedLabels.find(p => p.toLowerCase() === item.toLowerCase()))
        .filter((protein): protein is string => Boolean(protein))
        .filter(protein => !(newValue.favoriteProteins || []).includes(protein));

      const allNewProteins = [...predefinedToAdd, ...newProteins];
      if (allNewProteins.length > 0) {
        const currentAvoids = newValue.avoidProteins || [];
        newValue.favoriteProteins = [...(newValue.favoriteProteins || []), ...allNewProteins];
        newValue.avoidProteins = currentAvoids.filter(avoid =>
          !allNewProteins.some(newProtein => newProtein.toLowerCase() === avoid.toLowerCase())
        );
        updated = true;
      }
    }

    // Auto-add text from "Other proteins" (avoid) input
    if (avoidInput.trim()) {
      const predefinedLabels = commonProteins.map(p => p.label);
      const newAvoidProteins = avoidInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
        .filter(item => {
          const alreadyInAvoids = (newValue.avoidProteins || []).some(p => p.toLowerCase() === item.toLowerCase());
          const isPredefined = predefinedLabels.some(p => p.toLowerCase() === item.toLowerCase());
          return !alreadyInAvoids && !isPredefined;
        });

      const predefinedToAdd = avoidInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .map(item => predefinedLabels.find(p => p.toLowerCase() === item.toLowerCase()))
        .filter((protein): protein is string => Boolean(protein))
        .filter(protein => !(newValue.avoidProteins || []).includes(protein));

      const allNewAvoidProteins = [...predefinedToAdd, ...newAvoidProteins];
      if (allNewAvoidProteins.length > 0) {
        newValue.avoidProteins = [...(newValue.avoidProteins || []), ...allNewAvoidProteins];
        newValue.favoriteProteins = (newValue.favoriteProteins || []).filter(favorite =>
          !allNewAvoidProteins.some(newAvoid => newAvoid.toLowerCase() === favorite.toLowerCase())
        );
        updated = true;
      }
    }

    if (updated) {
      onProteinPreferencesChange(newValue);
      setPendingNext(true);
      return;
    }

    onNext();
  };

  // Separate predefined and custom proteins for display
  const predefinedLabels = commonProteins.map(p => p.label);
  const predefinedFavorites = (proteinPreferences.favoriteProteins || []).filter(p => predefinedLabels.includes(p));
  const customFavorites = (proteinPreferences.favoriteProteins || []).filter(p => !predefinedLabels.includes(p));
  const predefinedAvoids = (proteinPreferences.avoidProteins || []).filter(p => predefinedLabels.includes(p));
  const customAvoids = (proteinPreferences.avoidProteins || []).filter(p => !predefinedLabels.includes(p));

  // Curated protein suggestions for the "Other proteins" inputs, minus anything
  // the user has already picked (as a favorite or an avoid).
  const selectedProteinKeys = new Set(
    [...(proteinPreferences.favoriteProteins || []), ...(proteinPreferences.avoidProteins || [])]
      .map(p => p.toLowerCase())
  );
  const proteinSuggestions = PROTEIN_SUGGESTIONS.filter(p => !selectedProteinKeys.has(p.toLowerCase()));

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconMeat size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Protein preferences
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            Tell us about your protein preferences so we can create perfectly balanced meal plans
          </Text>
        </Stack>
      </Stack>

      {/* Favorite Proteins */}
      <Paper 
        radius="lg" 
        p={{ base: 'md', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="md">
          <Group gap="md" align="center">
            <ThemeIcon size={32} radius="md" color="green" variant="light">
              <IconFish size={18} />
            </ThemeIcon>
            <div style={{ flex: 1 }}>
              <Text fw={600} size="lg" c="dark">Favorite proteins</Text>
              <Text size="sm" c="dimmed">Select proteins you love eating</Text>
            </div>
          </Group>

          {/* Quick selection buttons */}
          <Group gap="xs" wrap="wrap" className="chip-cloud">
            {commonProteins.map(protein => {
              const isInAvoidList = predefinedAvoids.includes(protein.label);
              return (
                <Chip
                  key={protein.label}
                  checked={predefinedFavorites.includes(protein.label)}
                  onChange={() => toggleFavoriteProtein(protein.label)}
                  variant="outline"
                  color="green"
                  size="sm"
                  disabled={isInAvoidList}
                  style={{
                    minWidth: 'fit-content',
                    opacity: isInAvoidList ? 0.5 : 1
                  }}
                >
                  {protein.icon}<span style={{ marginLeft: '8px' }}>{protein.label}</span>
                </Chip>
              );
            })}
          </Group>

          {/* Other proteins input */}
          <Stack gap="xs">
            <Text fw={500} size="sm">Other proteins</Text>
            <FoodAutocompleteInput
              value={favoriteInput}
              onChange={setFavoriteInput}
              onAdd={addCustomFavorite}
              suggestions={proteinSuggestions}
              placeholder="e.g. Duck, Lamb, Quinoa..."
              color="green"
            />

            {/* Custom proteins display */}
            {customFavorites.length > 0 && (
              <Group gap="xs" wrap="wrap" className="chip-cloud">
                {customFavorites.map(protein => (
                  <Chip
                    key={protein}
                    checked={true}
                    onChange={() => removeFavorite(protein)}
                    variant="filled"
                    color="green"
                    size="sm"
                    style={{ minWidth: 'fit-content' }}
                  >
                    {protein}
                  </Chip>
                ))}
              </Group>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Avoid Proteins */}
      <Paper 
        radius="lg" 
        p={{ base: 'md', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="md">
          <Group gap="md" align="center">
            <ThemeIcon size={32} radius="md" color="red" variant="light">
              <IconMeatOff size={18} />
            </ThemeIcon>
            <div style={{ flex: 1 }}>
              <Text fw={600} size="lg" c="dark">Avoid proteins</Text>
              <Text size="sm" c="dimmed">Select proteins you want to avoid</Text>
            </div>
          </Group>

          {/* Quick selection buttons */}
          <Group gap="xs" wrap="wrap" className="chip-cloud">
            {commonProteins.map(protein => {
              const isInFavoritesList = predefinedFavorites.includes(protein.label);
              return (
                <Chip
                  key={protein.label}
                  checked={predefinedAvoids.includes(protein.label)}
                  onChange={() => toggleAvoidProtein(protein.label)}
                  variant="outline"
                  color="red"
                  size="sm"
                  disabled={isInFavoritesList}
                  style={{
                    minWidth: 'fit-content',
                    opacity: isInFavoritesList ? 0.5 : 1
                  }}
                >
                  {protein.icon}<span style={{ marginLeft: '8px' }}>{protein.label}</span>
                </Chip>
              );
            })}
          </Group>

          {/* Custom input */}
          <Stack gap="xs">
            <Text fw={500} size="sm">Other proteins</Text>
            <FoodAutocompleteInput
              value={avoidInput}
              onChange={setAvoidInput}
              onAdd={addCustomAvoid}
              suggestions={proteinSuggestions}
              placeholder="e.g. Duck, Lamb, Quinoa..."
              color="red"
            />

            {/* Custom avoided proteins display */}
            {customAvoids.length > 0 && (
              <Group gap="xs" wrap="wrap" className="chip-cloud">
                {customAvoids.map(protein => (
                  <Chip
                    key={protein}
                    checked={true}
                    onChange={() => removeAvoid(protein)}
                    variant="filled"
                    color="red"
                    size="sm"
                    style={{ minWidth: 'fit-content' }}
                  >
                    {protein}
                  </Chip>
                ))}
              </Group>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Protein Diversity Preference */}
      <Paper
        radius="lg"
        p={{ base: 'md', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%'
        }}
      >
        <Stack gap="md">
          <Text fw={600} size="lg">
            Protein variety per week
          </Text>
          <Text size="sm" c="dimmed">
            How much protein variety do you typically want in your meal plans?
          </Text>

          {/* Mobile: Vertical */}
          <SegmentedControl
            value={proteinPreferences.proteinDiversity || DEFAULT_PROTEIN_DIVERSITY}
            onChange={(value) => {
              onProteinPreferencesChange({
                ...proteinPreferences,
                proteinDiversity: value as "minimal" | "moderate" | "high"
              });
            }}
            data={[
              {
                label: 'Minimal (1-2)',
                value: 'minimal',
              },
              {
                label: 'Moderate (3-4)',
                value: 'moderate',
              },
              {
                label: 'High (5+)',
                value: 'high',
              },
            ]}
            orientation="vertical"
            size="md"
            color="orange"
            fullWidth
            hiddenFrom="sm"
            styles={{
              control: {
                padding: '8px 12px'
              }
            }}
          />

          {/* Desktop: Horizontal */}
          <SegmentedControl
            value={proteinPreferences.proteinDiversity || DEFAULT_PROTEIN_DIVERSITY}
            onChange={(value) => {
              onProteinPreferencesChange({
                ...proteinPreferences,
                proteinDiversity: value as "minimal" | "moderate" | "high"
              });
            }}
            data={[
              {
                label: 'Minimal (1-2)',
                value: 'minimal',
              },
              {
                label: 'Moderate (3-4)',
                value: 'moderate',
              },
              {
                label: 'High (5+)',
                value: 'high',
              },
            ]}
            orientation="horizontal"
            size="md"
            color="orange"
            visibleFrom="sm"
            styles={{
              control: {
                padding: '8px 12px'
              }
            }}
          />

          <Text size="sm" c="dimmed" ta="center" fw={500} mt="md">
            {proteinPreferences.proteinDiversity === "minimal" && "Keep it simple with the same proteins reused multiple times throughout the week"}
            {(proteinPreferences.proteinDiversity === "moderate" || !proteinPreferences.proteinDiversity) && "A balanced approach with variety while keeping shopping efficient"}
            {proteinPreferences.proteinDiversity === "high" && "Maximum variety with different proteins throughout the week"}
          </Text>
        </Stack>
      </Paper>

      {/* Summary */}
      {((proteinPreferences.favoriteProteins || []).length > 0 || (proteinPreferences.avoidProteins || []).length > 0) && (
        <Paper 
          p="lg" 
          radius="lg" 
          bg="gray.0" 
          style={{ 
            border: '1px solid var(--mantine-color-gray-2)',
            minHeight: 80,
            width: '100%'
          }}
        >
          <Group gap="md" align="center" style={{ height: '100%' }}>
            <ThemeIcon 
              size={40} 
              radius="md" 
              color="green" 
              variant="light"
            >
              <IconMeat size={20} />
            </ThemeIcon>
            
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} c="green.7" style={{ wordBreak: 'break-word' }}>
                Protein preferences saved!
              </Text>
              <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
                {(proteinPreferences.favoriteProteins || []).length > 0 && (
                  <>Loves: {(proteinPreferences.favoriteProteins || []).join(', ')}</>
                )}
                {(proteinPreferences.favoriteProteins || []).length > 0 && (proteinPreferences.avoidProteins || []).length > 0 && <br />}
                {(proteinPreferences.avoidProteins || []).length > 0 && (
                  <>Avoids: {(proteinPreferences.avoidProteins || []).join(', ')}</>
                )}
                {((proteinPreferences.favoriteProteins || []).length > 0 || (proteinPreferences.avoidProteins || []).length > 0) && <br />}
                Variety: {
                  proteinPreferences.proteinDiversity === "minimal" ? "Minimal (1-2)" :
                  proteinPreferences.proteinDiversity === "high" ? "High (5+)" :
                  "Moderate (3-4)"
                }
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button 
          variant="subtle" 
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          color="orange"
        >
          Back
        </Button>
        
        <Button
          size="md"
          rightSection={<IconArrowRight size={18} />}
          onClick={handleNext}
          color="orange"
        >
          {isLastStep ? 'Finish' : 'Continue'}
        </Button>
      </Group>
    </Stack>
  );
};
